import React from 'react';
import { motion } from 'framer-motion';

/**
 * スクロールで一度だけふわっと表示する共通ラッパー。
 * サイト全体でアニメーションの質感を揃えるために必ずこれを使う。
 */
export const Reveal: React.FC<{
  children: React.ReactNode;
  delay?: number;
  className?: string;
}> = ({ children, delay = 0, className }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/**
 * 「英字ラベル ＋ 日本語見出し ＋ リード文」の定型見出し。
 * 日本のBtoBサイトで標準的な構成。
 */
export const SectionHeading: React.FC<{
  eyebrow: string;
  title: React.ReactNode;
  lead?: React.ReactNode;
  align?: 'left' | 'center';
  tone?: 'dark' | 'light';
}> = ({ eyebrow, title, lead, align = 'center', tone = 'dark' }) => {
  const isCenter = align === 'center';
  return (
    <Reveal className={isCenter ? 'text-center' : 'text-left'}>
      <p className={`text-eyebrow uppercase num ${tone === 'light' ? 'text-brand-300' : 'text-brand-600'}`}>
        {eyebrow}
      </p>
      <h2 className={`text-h2 mt-3 ${tone === 'light' ? 'text-white' : 'text-ink-900'}`}>
        {title}
      </h2>
      {lead && (
        <p
          className={`text-lead mt-5 ${tone === 'light' ? 'text-ink-100' : 'text-body-soft'} ${
            isCenter ? 'mx-auto max-w-prose' : 'max-w-prose'
          }`}
        >
          {lead}
        </p>
      )}
    </Reveal>
  );
};

/**
 * 画面幅いっぱいのセクション。背景色のバリエーションを揃える。
 */
export const Section: React.FC<{
  id?: string;
  children: React.ReactNode;
  tone?: 'white' | 'muted' | 'ink';
  className?: string;
}> = ({ id, children, tone = 'white', className = '' }) => {
  const bg =
    tone === 'muted' ? 'bg-surface-muted' : tone === 'ink' ? 'bg-ink-900' : 'bg-white';
  return (
    <section id={id} className={`section ${bg} ${className}`}>
      <div className="container-content">{children}</div>
    </section>
  );
};
